import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { ParticleSystem, emitterConfigFromProps } from './ParticleSystem.js';

/**
 * PlatformerFxPreview — editor-only controller for particle-emitter previews.
 *
 * Split out of PlatformerBoard (1000-line rule) because it owns a cohesive
 * concern: the live in-editor ParticleSystem previews PLUS the authoring aids
 * layered on top of the SELECTED emitter so a designer can SEE what every
 * value affects without launching Test Play:
 *
 *   - Force gizmos: spawn box, emission cone, gravity, radial push/pull,
 *     tangential vortex, turbulence ring, start→end size circles, and the
 *     player-interaction range — each drawn at the emitter and summarised in
 *     a colour-coded legend so changing a slider shows an immediate overlay.
 *   - A labelled "PLAYER (demo)" ghost that sweeps across the selected
 *     emitter, driving the player-interaction modes (wake/push/attract/
 *     repel/swirl) so they animate live.
 *
 * The board delegates to one instance:
 *   board._fx.addPreview(layerC, entity)   // during init, per emitter
 *   board._fx.tick(delta)                  // per frame from the editor loop
 *   board._fx.setSelected(id | null)       // selection drives gizmos/ghost
 *   board._fx.updateLive(entity)           // tune a running emitter in place
 *   board._fx.clear()                       // board teardown
 *
 * updateLive() is the key to "don't stop and restart emitting": it pushes the
 * new config into the EXISTING ParticleSystem (setConfig keeps the live pool),
 * so the stream keeps flowing with the new values instead of being destroyed
 * and respawned by a full board rebuild.
 */

const DEG = Math.PI / 180;

// Colour key shared between the spatial gizmos and the legend so a force's
// overlay and its label always read as the same thing.
const GZ = {
    spawn: 0x60a5fa,    // blue   — emission box
    cone: 0xfbbf24,     // amber  — direction + spread
    gravity: 0x93c5fd,  // l.blue — gravity pull
    radial: 0xf472b6,   // pink   — push / pulse (radial)
    vortex: 0xa78bfa,   // violet — swirl (tangential)
    turb: 0x34d399,     // green  — turbulence
    size: 0xe2e8f0,     // white  — start→end size
    interact: 0x22d3ee, // cyan   — player interaction
};

function tagStyle(color, size = 10) {
    return new TextStyle({
        fontFamily: 'monospace', fontSize: size, fontWeight: '700', fill: color,
    });
}

function clampN(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

/** Straight arrow: shaft + two-line head, drawn in one stroke pass. */
function drawArrow(g, x1, y1, x2, y2, color, alpha, width) {
    const a = Math.atan2(y2 - y1, x2 - x1);
    const h = Math.max(5, width * 3);
    g.moveTo(x1, y1).lineTo(x2, y2)
        .moveTo(x2, y2).lineTo(x2 - h * Math.cos(a - 0.45), y2 - h * Math.sin(a - 0.45))
        .moveTo(x2, y2).lineTo(x2 - h * Math.cos(a + 0.45), y2 - h * Math.sin(a + 0.45))
        .stroke({ color, alpha, width, cap: 'round', join: 'round' });
}

/** 270° curved arrow showing swirl direction (cw=true → clockwise on screen). */
function drawSwirl(g, cx, cy, r, cw, color, alpha, width) {
    const steps = 28;
    const start = -Math.PI * 0.75;
    const sweep = Math.PI * 1.5;
    for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const ang = start + (cw ? sweep * t : -sweep * t);
        const x = cx + Math.cos(ang) * r, y = cy + Math.sin(ang) * r;
        if (s === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke({ color, alpha, width, cap: 'round', join: 'round' });
    const endAng = start + (cw ? sweep : -sweep);
    const ex = cx + Math.cos(endAng) * r, ey = cy + Math.sin(endAng) * r;
    const tang = endAng + (cw ? Math.PI / 2 : -Math.PI / 2);
    const h = Math.max(5, width * 3);
    g.moveTo(ex, ey).lineTo(ex - h * Math.cos(tang - 0.45), ey - h * Math.sin(tang - 0.45))
        .moveTo(ex, ey).lineTo(ex - h * Math.cos(tang + 0.45), ey - h * Math.sin(tang + 0.45))
        .stroke({ color, alpha, width, cap: 'round' });
}

/** Wobbly ring — turbulence churn made visible. */
function drawWavyRing(g, cx, cy, r, amp, color, alpha, width) {
    const steps = 56;
    for (let s = 0; s <= steps; s++) {
        const t = s / steps, ang = t * Math.PI * 2;
        const rr = r + Math.sin(ang * 6) * amp;
        const x = cx + Math.cos(ang) * rr, y = cy + Math.sin(ang) * rr;
        if (s === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke({ color, alpha, width, cap: 'round', join: 'round' });
}

export class PlatformerFxPreview {
    constructor(board) {
        this.board = board;
        /** @type {Array<{id:string, sys:ParticleSystem, layerC:any, cx:number, cy:number, props:object}>} */
        this._systems = [];
        this._byId = new Map();
        this._selectedId = null;
        this._selOverlay = null;    // { rec, gizmoC }  (ghost lives on sys._demo)
    }

    /** True when this emitter has any previews registered. */
    get hasSystems() { return this._systems.length > 0; }

    /**
     * Spin up a live preview ParticleSystem for an emitter entity and draw a
     * locator ring so a dormant emitter is still visible/clickable. The demo
     * ghost + force gizmos are NOT drawn here — they belong to the SELECTED
     * emitter only (see setSelected), which keeps unselected emitters quiet.
     */
    addPreview(layerC, e) {
        const cs = this.board.cellSize;
        const cx = e.c * cs + cs / 2;
        const cy = e.r * cs + cs / 2;

        const icon = new Graphics();
        icon.circle(cx, cy, cs * 0.30);
        icon.stroke({ color: GZ.interact, width: 1.5, alpha: 0.55 });
        icon.circle(cx, cy, cs * 0.14);
        icon.fill({ color: GZ.interact, alpha: 0.9 });
        layerC.addChild(icon);

        const fxCfg = emitterConfigFromProps(e.props || {});
        const sys = new ParticleSystem(layerC, fxCfg);
        sys.setOrigin(cx, cy);
        sys.setColliders(this._colliderCfg());
        if (e.props?.texture) {
            const src = this.board._theme?.get?.(e.props.texture);
            if (src) this.board._loadBgTexture(src, (tex) => sys.setTexture(tex));
        }

        const rec = { id: e.id, sys, layerC, cx, cy, props: e.props || {} };
        this._systems.push(rec);
        this._byId.set(e.id, rec);
        return sys;
    }

    /** Tile-grid collider closures shared by every preview system. */
    _colliderCfg() {
        const board = this.board;
        return {
            cellSize: board.cellSize,
            solidAt: (c, r) => board.isSolidAt(c, r),
            oneWayAt: (c, r) => board.isOneWayAt(c, r),
        };
    }

    /**
     * Advance every preview emitter. `delta` is in 60 FPS frames; particles
     * think in seconds. Sweeps the selected emitter's demo ghost so the
     * player-interaction modes animate.
     */
    tick(delta) {
        if (!this._systems.length) return;
        const dt = (delta || 1) / 60;
        for (const rec of this._systems) {
            const sys = rec.sys;
            const d = sys._demo;
            if (d) {
                d.t += dt;
                const gx = d.cx + Math.sin(d.t * d.omega) * d.range;
                d.body.vx = Math.cos(d.t * d.omega) * d.range * d.omega;
                d.body.vy = 0;
                d.body.x = gx - d.bw / 2;
                d.body.y = d.cy - d.bh / 2;
                d.ghost.position.set(gx, d.cy);
            }
            sys.update(dt);
        }
    }

    /**
     * Re-point the gizmos + demo ghost at a newly selected emitter (or clear
     * them when nothing/!emitter is selected). Idempotent — re-selecting the
     * same emitter is a no-op so the ghost keeps sweeping smoothly.
     */
    setSelected(id) {
        if (id === this._selectedId) return;
        this._clearSelectionOverlay();
        this._selectedId = id || null;
        if (!this._selectedId) return;
        const rec = this._byId.get(this._selectedId);
        if (!rec) { this._selectedId = null; return; }
        const cfg = emitterConfigFromProps(rec.props || {});
        this._selOverlay = { rec, gizmoC: null };
        this._buildGizmo(rec, cfg);
        this._syncDemo(rec, cfg);
    }

    /**
     * Push tuned props into a RUNNING preview without a rebuild, so emission
     * never stops/restarts. Returns false when the edit flips the render node
     * type (PNG image on/off) — the pool can't be re-typed in place, so the
     * caller falls back to a full board rebuild.
     */
    updateLive(entity) {
        const rec = this._byId.get(entity.id);
        if (!rec) return false;
        const cfg = emitterConfigFromProps(entity.props || {});
        if (!!cfg.image !== !!rec.sys.cfg.image) return false;  // node-type flip → rebuild
        rec.props = entity.props || {};
        rec.sys.setConfig(cfg);
        rec.sys.setColliders(this._colliderCfg());
        if (cfg.image && entity.props?.texture) {
            const src = this.board._theme?.get?.(entity.props.texture);
            if (src) this.board._loadBgTexture(src, (tex) => rec.sys.setTexture(tex));
        }
        if (entity.id === this._selectedId) {
            this._buildGizmo(rec, cfg);
            this._syncDemo(rec, cfg);
        }
        return true;
    }

    /** Tear down every preview system + overlay (board destroy). */
    clear() {
        this._clearSelectionOverlay();
        for (const rec of this._systems) rec.sys.destroy();
        this._systems.length = 0;
        this._byId.clear();
        this._selectedId = null;
    }

    // ── selection overlay ──────────────────────────────

    _clearSelectionOverlay() {
        const ov = this._selOverlay;
        if (!ov) return;
        if (ov.gizmoC) {
            if (ov.gizmoC.parent) ov.gizmoC.parent.removeChild(ov.gizmoC);
            ov.gizmoC.destroy({ children: true });
        }
        const sys = ov.rec?.sys;
        if (sys) {
            const ghost = sys._demo?.ghost;
            if (ghost) {
                if (ghost.parent) ghost.parent.removeChild(ghost);
                ghost.destroy({ children: true });
            }
            sys._demo = null;
            sys.setBodyProvider(null);
        }
        this._selOverlay = null;
    }

    /**
     * Attach / refresh / remove the moving "PLAYER (demo)" ghost for the
     * selected emitter, depending on whether player interaction is enabled.
     * Preserves the sweep clock across refreshes so tuning a value mid-sweep
     * doesn't snap the ghost back to centre.
     */
    _syncDemo(rec, cfg) {
        const on = cfg.playerInteract && cfg.playerInteract !== 'off' && cfg.interactStrength > 0;
        const sys = rec.sys;
        if (!on) {
            const ghost = sys._demo?.ghost;
            if (ghost) {
                if (ghost.parent) ghost.parent.removeChild(ghost);
                ghost.destroy({ children: true });
            }
            sys._demo = null;
            sys.setBodyProvider(null);
            return;
        }

        const cs = this.board.cellSize;
        const bw = cs * 0.8, bh = cs * 1.25;
        const range = clampN(cfg.interactRadius * 0.9, cs * 1.5, cs * 4);
        const keepT = sys._demo?.t || 0;

        // Reuse the existing ghost container, just redraw it, so a live tune
        // doesn't flicker. Build fresh on first attach.
        let ghost = sys._demo?.ghost;
        let gfx, tag;
        if (ghost) {
            [gfx, tag] = ghost.children;
            gfx.clear();
        } else {
            ghost = new Container();
            ghost.zIndex = 201;     // above gizmo + particles
            gfx = new Graphics();
            tag = new Text({ text: 'PLAYER (demo)', style: tagStyle(0x7dd3fc, 9) });
            tag.anchor.set(0.5, 1);
            ghost.addChild(gfx);
            ghost.addChild(tag);
            // Overlay layer: the board root container is sortableChildren and
            // shares the layers' coordinate space (layers sit at origin), so a
            // high zIndex keeps the ghost on top of every particle.
            this.board.container.addChild(ghost);
        }
        gfx.circle(0, 0, cfg.interactRadius).stroke({ color: GZ.interact, width: 1, alpha: 0.25 });
        gfx.roundRect(-bw / 2, -bh / 2, bw, bh, 4)
            .fill({ color: 0x38bdf8, alpha: 0.30 })
            .stroke({ color: 0x7dd3fc, width: 1.5, alpha: 0.65 });
        tag.position.set(0, -bh / 2 - 4);
        ghost.position.set(rec.cx, rec.cy);

        const body = sys._demo?.body || { x: rec.cx, y: rec.cy, w: bw, h: bh, vx: 0, vy: 0 };
        body.w = bw; body.h = bh;
        sys.setBodyProvider(() => [body]);
        sys._demo = { cx: rec.cx, cy: rec.cy, range, omega: 1.6, t: keepT, bw, bh, ghost, body };
    }

    /**
     * Draw the force gizmos for the selected emitter: a spatial overlay (cone,
     * rings, arrows, size circles) anchored at the emitter PLUS a colour-coded
     * legend that lists every ACTIVE force with its value, so adjusting any
     * slider produces an instant, readable visual.
     */
    _buildGizmo(rec, cfg) {
        const ov = this._selOverlay;
        if (!ov) return;
        // Reuse the container across refreshes; rebuild its contents.
        let gizmoC = ov.gizmoC;
        if (gizmoC) {
            gizmoC.removeChildren().forEach((ch) => ch.destroy({ children: true }));
        } else {
            gizmoC = new Container();
            gizmoC.zIndex = 200;    // overlay above tiles + particles
            // Drawn into the board root (sortableChildren, shares the layers'
            // origin) so the gizmos always sit on top of the live particles.
            this.board.container.addChild(gizmoC);
            ov.gizmoC = gizmoC;
        }

        const cs = this.board.cellSize;
        const cx = rec.cx, cy = rec.cy;
        const g = new Graphics();
        gizmoC.addChild(g);
        const legend = [];   // { text, color }

        // 1. Spawn box — where particles are born.
        if (cfg.spawnW > 0 || cfg.spawnH > 0) {
            const w = Math.max(cfg.spawnW, 2), h = Math.max(cfg.spawnH, 2);
            g.rect(cx - w / 2, cy - h / 2, w, h).stroke({ color: GZ.spawn, width: 1, alpha: 0.7 });
            legend.push({ text: `\u25CF Spawn box ${Math.round(cfg.spawnW)}\u00D7${Math.round(cfg.spawnH)}`, color: GZ.spawn });
        }

        // 2. Emission cone — direction (0°=up, clockwise) + spread width.
        {
            const L = clampN(cfg.speedMax * 0.18, cs * 0.8, cs * 3);
            const base = cfg.angle * DEG;
            const half = (cfg.spread * DEG) / 2;
            const dir = (a) => [cx + Math.sin(a) * L, cy - Math.cos(a) * L];
            const [mx, my] = dir(base);
            drawArrow(g, cx, cy, mx, my, GZ.cone, 0.95, 2);
            if (cfg.spread > 0) {
                const [lx, ly] = dir(base - half);
                const [rx, ry] = dir(base + half);
                g.moveTo(cx, cy).lineTo(lx, ly).moveTo(cx, cy).lineTo(rx, ry)
                    .stroke({ color: GZ.cone, width: 1, alpha: 0.45 });
            }
            legend.push({ text: `\u25CF Aim ${Math.round(cfg.angle)}\u00B0 \u00B1${Math.round(cfg.spread / 2)}\u00B0`, color: GZ.cone });
        }

        // 3. Gravity — vertical pull.
        if (cfg.gravity !== 0) {
            const down = cfg.gravity > 0;
            const L = clampN(Math.abs(cfg.gravity) * 0.06, cs * 0.4, cs * 2.4);
            drawArrow(g, cx + cs * 0.9, cy, cx + cs * 0.9, cy + (down ? L : -L), GZ.gravity, 0.8, 2);
            legend.push({ text: `\u25CF Gravity ${Math.round(cfg.gravity)}`, color: GZ.gravity });
        }

        // 4. Radial — push (outward, +) / pulse-in (inward, −).
        if (cfg.radialAccel !== 0) {
            const out = cfg.radialAccel > 0;
            const r0 = cs * 0.7;
            const r1 = clampN(Math.abs(cfg.radialAccel) * 0.01 + cs * 0.4, cs * 0.95, cs * 1.8);
            for (let k = 0; k < 6; k++) {
                const a = k * Math.PI / 3;
                const ax = Math.cos(a), ay = Math.sin(a);
                const sx = cx + ax * (out ? r0 : r1), sy = cy + ay * (out ? r0 : r1);
                const ex = cx + ax * (out ? r1 : r0), ey = cy + ay * (out ? r1 : r0);
                drawArrow(g, sx, sy, ex, ey, GZ.radial, 0.85, 1.5);
            }
            legend.push({ text: `\u25CF ${out ? 'Push out' : 'Pull in'} ${Math.round(Math.abs(cfg.radialAccel))}`, color: GZ.radial });
        }

        // 5. Tangential — swirl/vortex. +ve → clockwise on screen.
        if (cfg.tangentialAccel !== 0) {
            const cw = cfg.tangentialAccel > 0;
            drawSwirl(g, cx, cy, cs * 1.05, cw, GZ.vortex, 0.9, 2);
            legend.push({ text: `\u25CF Vortex ${Math.round(Math.abs(cfg.tangentialAccel))} ${cw ? 'CW' : 'CCW'}`, color: GZ.vortex });
        }

        // 6. Turbulence — churn radius wobbles with strength.
        if (cfg.turbulence !== 0) {
            const amp = clampN(Math.abs(cfg.turbulence) * 0.04, 2, cs * 0.5);
            drawWavyRing(g, cx, cy, cs * 1.35, amp, GZ.turb, 0.75, 1.5);
            legend.push({ text: `\u25CF Turbulence ${Math.round(Math.abs(cfg.turbulence))}`, color: GZ.turb });
        }

        // 7. Size — start (solid) → end (dashed-feel faint) radius circles.
        {
            const rs = Math.max(1, cfg.startSize), re = Math.max(0.5, cfg.endSize);
            g.circle(cx, cy, rs).stroke({ color: GZ.size, width: 1.5, alpha: 0.85 });
            g.circle(cx, cy, re).stroke({ color: GZ.size, width: 1, alpha: 0.4 });
            legend.push({ text: `\u25CF Size ${Math.round(cfg.startSize)}\u2192${Math.round(cfg.endSize)}`, color: GZ.size });
        }

        // 8. Player interaction reach + mode (ghost drives it live).
        if (cfg.playerInteract && cfg.playerInteract !== 'off' && cfg.interactStrength > 0) {
            g.circle(cx, cy, cfg.interactRadius).stroke({ color: GZ.interact, width: 1, alpha: 0.35 });
            legend.push({ text: `\u25CF Interact: ${cfg.playerInteract} r${Math.round(cfg.interactRadius)} \u00D7${cfg.interactStrength}`, color: GZ.interact });
        }

        // Legend stack — anchored to the right of the emitter so the overlay
        // is self-documenting. Each line is its own colour to match its gizmo.
        const lx = cx + cs * 1.7;
        let ly = cy - cs * 1.6;
        const title = new Text({ text: 'EMITTER FX', style: tagStyle(0xffffff, 10) });
        title.position.set(lx, ly);
        gizmoC.addChild(title);
        ly += 14;
        for (const item of legend) {
            const t = new Text({ text: item.text, style: tagStyle(item.color, 10) });
            t.position.set(lx, ly);
            gizmoC.addChild(t);
            ly += 13;
        }
    }
}
